import { create } from "zustand";
import { DEFAULT_MAP_NOTE_PIN_COLOR, type MapNotePinColor } from "@west-marches/shared";

interface MapNoteStoreState {
  hoveredMapNoteId: string | null;
  editingMapNoteId: string | null;
  /**
   * When non-null, the GM has entered "move mode" for the given map note. The
   * next valid hex click on the map will move the pin and exit the mode. The
   * popover is hidden while moving.
   */
  movingMapNoteId: string | null;
  popoverScreenX: number;
  popoverScreenY: number;
  /** Color used when the GM drops the next pin with the map note tool. */
  pendingPinColor: MapNotePinColor;
  /** Whether the next dropped map note should use the faint hidden-pin treatment. */
  pendingHidden: boolean;
  /**
   * Template applied to the next pin dropped with the map note tool. Sticky
   * until the user clears it from the tool panel. In-memory only (resets on
   * reload).
   */
  pendingTemplateId: string | null;
}

interface MapNoteStoreActions {
  showPopover: (mapNoteId: string, screenX: number, screenY: number) => void;
  hidePopover: () => void;
  /** Hide the hover popover unless a map note is being edited. */
  scheduleHide: () => void;
  /** Cancel any pending hide work. */
  cancelHide: () => void;
  startEditing: (mapNoteId: string, screenX: number, screenY: number) => void;
  stopEditing: () => void;
  /**
   * Enter move mode for the given map note. Closes any open popover/edit state
   * so the GM can click a destination hex.
   */
  startMoving: (mapNoteId: string) => void;
  /** Exit move mode without moving. */
  stopMoving: () => void;
  setPendingPinColor: (color: MapNotePinColor) => void;
  setPendingHidden: (hidden: boolean) => void;
  setPendingTemplateId: (templateId: string | null) => void;
}

type MapNoteStore = MapNoteStoreState & MapNoteStoreActions;

let hideTimer: ReturnType<typeof setTimeout> | null = null;

function clearHideTimer() {
  if (hideTimer !== null) {
    clearTimeout(hideTimer);
    hideTimer = null;
  }
}

export const useMapNoteStore = create<MapNoteStore>((set, get) => ({
  hoveredMapNoteId: null,
  editingMapNoteId: null,
  movingMapNoteId: null,
  popoverScreenX: 0,
  popoverScreenY: 0,
  pendingPinColor: DEFAULT_MAP_NOTE_PIN_COLOR,
  pendingHidden: false,
  pendingTemplateId: null,

  showPopover(mapNoteId, screenX, screenY) {
    clearHideTimer();
    set((s) => {
      if (s.editingMapNoteId) return s;
      return { hoveredMapNoteId: mapNoteId, popoverScreenX: screenX, popoverScreenY: screenY };
    });
  },

  hidePopover() {
    clearHideTimer();
    set((s) => {
      if (s.editingMapNoteId) return s;
      return { hoveredMapNoteId: null };
    });
  },

  scheduleHide() {
    if (get().editingMapNoteId) return;
    clearHideTimer();
    // Defer to the next macrotask so a synchronous re-entry into the popover
    // (e.g. moving from the map note hex into the popover that overlaps it)
    // can cancel the hide via `cancelHide()` before it actually runs. This
    // avoids a mount/unmount flicker without introducing a perceptible delay
    // when the cursor truly leaves to empty space.
    hideTimer = setTimeout(() => {
      hideTimer = null;
      get().hidePopover();
    }, 0);
  },

  cancelHide() {
    clearHideTimer();
  },

  startEditing(mapNoteId, screenX, screenY) {
    clearHideTimer();
    set({
      editingMapNoteId: mapNoteId,
      hoveredMapNoteId: mapNoteId,
      popoverScreenX: screenX,
      popoverScreenY: screenY,
    });
  },

  stopEditing() {
    clearHideTimer();
    set({ editingMapNoteId: null, hoveredMapNoteId: null });
  },

  startMoving(mapNoteId) {
    clearHideTimer();
    set({
      movingMapNoteId: mapNoteId,
      editingMapNoteId: null,
      hoveredMapNoteId: null,
    });
  },

  stopMoving() {
    set({ movingMapNoteId: null });
  },

  setPendingPinColor(color) {
    set({ pendingPinColor: color });
  },

  setPendingHidden(hidden) {
    set({ pendingHidden: hidden });
  },

  setPendingTemplateId(templateId) {
    set({ pendingTemplateId: templateId });
  },
}));
